/** @license
 *
 * Copyright 2022 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

import "./styles/calcite-web.css";
import "./styles/esri-global-nav.css";
import "./styles/styles.css";
import "isomorphic-fetch";

import * as launcher from "./launcher";
import { createJSONLoader, registerMessageBundleLoader } from "@arcgis/core/intl";
import { decideAndSetLocale, decideAndSetDir } from "./t9nUtils";
import { defineCustomElements } from "@esri/calcite-components/dist/loader";
import { setAssetPath } from "@esri/calcite-components/dist/components";
import { LogLevel } from "./Logger";

import "@esri/solutions-components";
import "@esri/solutions-components/dist/components/consent-manager";

import "@arcgis/analysis-components/dist/components/analysis-spatial-reference-selector";

import "@esri/solution-common";
import "@esri/solution-creator";
import "@esri/solution-deployer";
import "@esri/solution-feature-layer";
import "@esri/solution-file";
import "@esri/solution-form";
import "@esri/solution-group";
import "@esri/solution-hub-types";
import "@esri/solution-simple-types";
import "@esri/solution-storymap";
import "@esri/solution-velocity";
import "@esri/solution-viewer";
import "@esri/solution-web-experience";

import "@esri/calcite-components";
import "@esri/calcite-components/dist/calcite/calcite.css";

import * as ac from "@arcgis/analysis-components/dist/components";

(function init() {
  // Init Calcite
  void defineCustomElements(window);
  setAssetPath(location.href);

  // Restore console.info now that Calcite has been initialized
  console.info = (window as any).saveConsoleInfo;

  // Local analysis components assets
  ac.setAssetPath(location.href); // PATH depends on framework, more info below

  // Get the locale
  const locale = decideAndSetLocale(window?.location?.search, undefined, window?.navigator?.language);

  // Detect unsupported browsers
  if (!HTMLElement.prototype.attachShadow) {
    if (window.location.hostname.indexOf('arcgis.com') > -1) {
      window.location.replace(`error.html?error=browser&locale=${locale}`);
    }
    else{
      window.location.replace(`error.html?error=browser_enterprise&locale=${locale}`);
    }
  }

  // Check for blocked access
  const hashParamsOnLoad = new URLSearchParams(window.location.search);
  if (hashParamsOnLoad.get("messageCode") === "OAUTH_0070"){
    window.location.replace(`error.html?error=blocked&locale=${locale}`);
  }

  // Get the app config file and launch the app
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  fetch("./appConfig.json").then(
    appConfigFile => appConfigFile.json()
  ).then(
    appConfigJson => {
      const appConfig = prepareAppConfig(appConfigJson as launcher.IAppConfigFile, locale);
      configureIntlLoader();
      void launcher.launch(appConfig);
    }
  );
})();

/**
 * Configures the JSAPI locale loader.
 */
function configureIntlLoader(
): void {
  registerMessageBundleLoader(
    createJSONLoader({
      pattern: "deployment/",
      base: "",
      location: new URL("./assets/", window.location.href)
    })
  );
}

/**
 * Gets the value of a cookie.
 * @param name Name of cookie to fetch
 * @returns Value of cookie or `undefined` if not found
 */
function getCookie(
  name: string
): string | undefined {
  return document.cookie
    .split("; ")
    .find(cookie => cookie.startsWith(`${name}=`))
    ?.split("=")[1];
}

/**
 * Prepares the app configuration file for use in the app.
 *
 * @param appConfigFile Runtime configuration file
 * @param locale Locale to set
 * @return Copy of configuration file with `locale` and `isRTL` properties set
 */
function prepareAppConfig(
  appConfigFile: launcher.IAppConfigFile,
  locale="en" // Default locale
): launcher.IAppConfigFile {
  const suppress_solutionDiagramFeature_announcement =
    getCookie("suppress_solutionDiagramFeature_announcement") === "true";

  const appConfig: launcher.IAppConfigFile = {
    appId: appConfigFile.appId,
    portalURL: appConfigFile.portalURL,
    primarySolutionsGroupId: appConfigFile.primarySolutionsGroupId,
    agoBasedEnterpriseSolutionsGroupId: appConfigFile.agoBasedEnterpriseSolutionsGroupId,

    consoleMessageLevel:
      Math.min(
        Math.max(0,
          Math.round(appConfigFile.consoleMessageLevel)
        ),
        LogLevel.Info
      ),
    cookiesFirstUseVar: appConfigFile.cookiesFirstUseVar,
    isRTL: decideAndSetDir(locale),
    locale,
    measurementIds: appConfigFile.measurementIds,
    suppress_solutionDiagramFeature_announcement,
    trackingEnabled: appConfigFile.trackingEnabled,
    forceTrackingEnv: appConfigFile.forceTrackingEnv,
    telemetry: appConfigFile.telemetry
  };
  return appConfig;
}
